/**
 * Product types on the client (D32). The registry comes from the server
 * (GET /api/meta/product-types, lib/catalog/productTypes.js); forms, spec lines
 * and (P3) filters are generated from it. Helpers here are pure.
 */
import { useQuery } from '@/lib/useQuery';

/**
 * { types, byKey, checklists, conditions, loading, error } — the registry, fetched once per page.
 * `conditions` (D98, lib/catalog/conditions): [{ key, label, badge, requires, warranty_months_default }].
 */
export function useProductTypes() {
  const q = useQuery('productTypes', undefined, { select: (r) => r });
  const types = (q.data && q.data.types) || [];
  return {
    ...q, types, byKey: Object.fromEntries(types.map((t) => [t.key, t])),
    checklists: (q.data && q.data.checklists) || {}, conditions: (q.data && q.data.conditions) || [],
  };
}

const parse = (v) => {
  if (v == null || v === '') return {};
  if (typeof v === 'object') return v;
  try { return JSON.parse(v) || {}; } catch { return {}; }
};

/** A model row's attributes (attributes_json), with laptop-era columns as a fallback. */
export function modelAttributes(row = {}) {
  const fromColumns = Object.fromEntries(
    ['cpu_family', 'cpu_generation', 'ram_gb', 'storage_gb', 'storage_type', 'display_size', 'os']
      .filter((k) => row[k] != null && row[k] !== '').map((k) => [k, row[k]])
  );
  return { ...fromColumns, ...parse(row.attributes_json) };
}

/**
 * The store's own fields of a type (ADR 0009 §4, MERCH-86): `type.custom_attributes`, set by
 * withCustom from the store's definitions. Same attribute schema as the platform's; values
 * live in attributes.custom, and in forms under `form.custom`.
 */
export const customFieldsOf = (type) => (type && Array.isArray(type.custom_attributes) ? type.custom_attributes : []);

/** A registry type with the store's live custom fields attached (none: an empty list). */
export function withCustom(type, fields) {
  if (!type) return type;
  return { ...type, custom_attributes: Array.isArray(fields) ? fields : [] };
}

/**
 * The store's custom fields (Category.customAttributes, staff): { types: { <type>: [field…] },
 * kinds, max, loading, error, reload }. `typeOf(key, registry)` gives the registry type with them.
 */
// Stable empties: a fresh {} or [] per render would re-run every effect that depends on them.
const NO_TYPES = Object.freeze({});
const NO_KINDS = Object.freeze([]);

export function useStoreCustomFields({ enabled = true } = {}) {
  const q = useQuery('storeCustomAttributes', {}, { select: (r) => (r && r.data) || {}, enabled });
  const types = (q.data && q.data.types) || NO_TYPES;
  return { ...q, types, kinds: (q.data && q.data.kinds) || NO_KINDS, max: (q.data && q.data.max_fields) || 20, withFields: (type) => withCustom(type, type ? types[type.key] : []) };
}

/**
 * Yes/no words: the same list as the server's (lib/catalog/productTypes YES_WORDS / NO_WORDS,
 * pinned by tests on both sides). A boolean or 1/0 as itself; anything else is null.
 */
export const YES_WORDS = Object.freeze(['yes', 'y', 'true', '1']);
export const NO_WORDS = Object.freeze(['no', 'n', 'false', '0']);
export function parseYesNo(v) {
  if (v === true || v === false) return v;
  if (v === 1 || v === 0) return v === 1;
  if (typeof v !== 'string') return null;
  const s = v.trim().toLowerCase();
  return YES_WORDS.includes(s) ? true : NO_WORDS.includes(s) ? false : null;
}

/** A number rounded half-up in decimal (13.95 → 14.0 at 1), as the server rounds on write (lib/catalog/productTypes roundTo). */
export function roundTo(n, decimals) {
  if (decimals == null || !Number.isFinite(n)) return n;
  const shifted = String(n).includes('e') ? Math.round(n * 10 ** decimals) : Math.round(Number(`${n}e${decimals}`));
  return shifted / 10 ** decimals;
}

const formValue = (a, v) => {
  if (a.kind === 'boolean') return parseYesNo(v) === true;
  return v == null ? '' : String(Number.isFinite(Number(v)) && a.kind === 'number' ? Number(v) : v);
};

/** Attribute values → form strings (inputs hold strings; booleans stay booleans); custom ones under `custom`. */
export function formFromAttributes(type, attrs = {}) {
  if (!type) return {};
  const form = Object.fromEntries(type.attributes.map((a) => [a.key, formValue(a, attrs[a.key])]));
  const own = customFieldsOf(type);
  if (own.length) {
    const stored = attrs.custom && typeof attrs.custom === 'object' ? attrs.custom : {};
    form.custom = Object.fromEntries(own.map((a) => [a.key, formValue(a, stored[a.key])]));
  }
  return form;
}

/** One form value for the server: undefined when blank (a boolean is always sent). */
function serverValue(a, v) {
  if (a.kind === 'boolean') return v === true || v === false ? v : undefined;
  if (v == null || String(v).trim() === '') return undefined;
  return a.kind === 'number' ? Number(v) : String(v).trim();
}

/**
 * Form → attributes for the server (blanks dropped, numbers as numbers). The server validates again.
 * Custom fields go in `custom`; a blank one is sent as '' so an edit clears it.
 */
export function attributesFromForm(type, form = {}) {
  if (!type) return {};
  const out = {};
  for (const a of type.attributes) {
    const v = serverValue(a, form[a.key]);
    if (v !== undefined) out[a.key] = v;
  }
  const own = customFieldsOf(type);
  if (own.length) {
    const given = form.custom && typeof form.custom === 'object' ? form.custom : {};
    out.custom = Object.fromEntries(own.map((a) => { const v = serverValue(a, given[a.key]); return [a.key, v === undefined ? '' : v]; }));
  }
  return out;
}

/** One value's problem against its field (required, number range), or ''. */
function fieldError(a, v) {
  const blank = a.kind === 'boolean' ? false : v == null || String(v).trim() === '';
  if (blank) return a.required ? `${a.label} is required` : '';
  if (a.kind === 'number') {
    const n = Number(v);
    if (!Number.isFinite(n)) return 'Enter a number';
    if (a.min != null && n < a.min) return `At least ${a.min}${a.unit ? ` ${a.unit}` : ''}`;
    if (a.max != null && n > a.max) return `At most ${a.max}${a.unit ? ` ${a.unit}` : ''}`;
  }
  return '';
}

/** A custom value outside its kind's choices (a CSV cell): yes/no, or one of a pick list's options. */
function choiceError(a, v) {
  if (v == null || v === '') return '';
  if (a.kind === 'boolean' && parseYesNo(v) === null) return `${a.label} must be yes or no`;
  if (a.kind === 'select' && !(a.options || []).some((o) => o.toLowerCase() === String(v).trim().toLowerCase())) return `${a.label} must be one of: ${(a.options || []).join(', ')}`;
  return '';
}

/** Client-side check mirroring the server rules (required, number range). { key: message }; custom ones as `custom.<key>`. */
export function validateAttributes(type, form = {}) {
  const e = {};
  if (!type) return e;
  for (const a of type.attributes) {
    const msg = fieldError(a, form[a.key]);
    if (msg) e[a.key] = msg;
  }
  const given = form.custom && typeof form.custom === 'object' ? form.custom : {};
  for (const a of customFieldsOf(type)) {
    const msg = fieldError(a, given[a.key]) || choiceError(a, given[a.key]);
    if (msg) e[`custom.${a.key}`] = msg;
  }
  return e;
}

/**
 * The store's custom values of a model or listing to show (product page): [{ key, label, value }]
 * for live fields with a value, in the store's order. A deleted field is never shown.
 */
export function customRows(type, attrs = {}) {
  const stored = attrs && attrs.custom && typeof attrs.custom === 'object' ? attrs.custom : {};
  return customFieldsOf(type).map((a) => ({ key: a.key, label: a.label, value: formatValue(a, stored[a.key]) })).filter((r) => r.value);
}

/** One attribute value for display: "16 GB", "Yes", "Android". */
export function formatValue(a, v) {
  if (v == null || v === '') return '';
  if (a.kind === 'boolean') { const b = parseYesNo(v); return b === null ? '' : b ? 'Yes' : 'No'; }
  if (a.kind === 'number') {
    const n = Number(v);
    const num = Number.isFinite(n) ? String(roundTo(n, a.decimals)) : String(v);
    return a.unit === 'in' ? `${num}"` : a.unit ? `${num} ${a.unit}` : num;
  }
  return String(v);
}

const blank = (v) => v == null || v === '' || v === false;
const toNumber = (v) => (blank(v) ? NaN : Number(v));

/**
 * Formats a template slot may name ({key:format}). A format returns '' for a value it
 * cannot show, which drops a template whose first slot it is. Extend by adding an entry.
 */
const FORMATS = {
  // The value as stored; 0 counts as blank, as laptop spec lines always treated it.
  text: (v) => (blank(v) || v === 0 || v === '0' ? '' : String(v)),
  // Gigabytes as "512GB" or "1TB".
  bytes: (v) => { const n = toNumber(v); return Number.isFinite(n) && n > 0 ? (n >= 1024 ? `${n / 1024}TB` : `${n}GB`) : ''; },
  // A positive number to one decimal place, half-up as the server stores it ("14", "15.6"; 13.95 → "14").
  dp1: (v) => { const n = toNumber(v); return Number.isFinite(n) && n > 0 ? String(roundTo(n, 1)) : ''; },
};
/** The format names a template may use (the registry is checked against these in tests). */
export const FORMAT_KEYS = Object.freeze(Object.keys(FORMATS));
/** Every `{key:format}` slot of a template → [{ key, format }] (format null when none). */
export const templateSlots = (template) => [...String(template || '').matchAll(/\{(\w+)(?::(\w+))?\}/g)].map(([, key, format]) => ({ key, format: format || null }));

/**
 * One registry view template (lib/catalog/productTypes `views`, `facet_template`) filled from
 * attribute values: '{ram_gb}GB RAM' → "16GB RAM". '' when the first slot is blank; blanks in
 * later slots are dropped and the spacing tidied ("512GB SSD", or "512GB" with no storage type).
 */
export function renderTemplate(template, values = {}) {
  const src = String(template || '');
  const slots = [...src.matchAll(/\{(\w+)(?::(\w+))?\}/g)];
  const fill = ([, key, format]) => (FORMATS[format] || FORMATS.text)(values[key]);
  if (slots.length && !fill(slots[0])) return '';
  return src.replace(/\{(\w+)(?::(\w+))?\}/g, (...m) => fill(m)).replace(/\s+/g, ' ').trim();
}

/** [{ ...entry, value }] for a view's entries that render, or null when the type declares no `views[name]`. */
export function viewRows(type, name, attrs = {}) {
  const entries = (type && type.views && type.views[name]) || null;
  if (!entries) return null;
  return entries
    .map((e) => (typeof e === 'string' ? { text: e } : e))
    .map((e) => ({ ...e, value: renderTemplate(e.text, attrs) }))
    .filter((e) => e.value);
}

/** The spec form's starting values for a type: each attribute's declared `default`, else blank. */
export function defaultSpecs(type) {
  if (!type) return {};
  const base = formFromAttributes(type, {});
  for (const a of type.attributes) if (a.default != null) base[a.key] = a.kind === 'boolean' ? Boolean(a.default) : String(a.default);
  return base;
}

/**
 * The short spec line of a model or listing: its type's `views.card` templates when it declares
 * them (laptops: "Core i5 11th Gen · 16GB RAM · 512GB SSD · 14\""), else its `card` attributes
 * ("128 GB · 5G" …). '' without a type: nothing is guessed (ADR 0009 §1). One line for the
 * back office and the storefront.
 */
export function specLine(type, attrs = {}) {
  if (!type) return '';
  const view = viewRows(type, 'card', attrs);
  if (view) return view.map((e) => e.value).join(' · ');
  return type.attributes.filter((a) => a.card).map((a) => formatValue(a, attrs[a.key])).filter(Boolean).join(' · ');
}

/** The serial field's label and hint for a type. */
export function serialLabel(type) {
  return type && type.serial === 'imei' ? { label: 'IMEI', hint: '15 digits. Dial *#06# on the device to see it.' } : { label: 'Serial number', hint: '' };
}

/** 15 digits with a valid Luhn check digit (mirrors lib/catalog/serials.isImei). */
export function isImei(v) {
  const s = String(v == null ? '' : v).replace(/[\s-]+/g, '');
  if (!/^\d{15}$/.test(s)) return false;
  let sum = 0;
  for (let i = 0; i < 15; i += 1) {
    let d = Number(s[14 - i]);
    if (i % 2 === 1) d = d * 2 > 9 ? d * 2 - 9 : d * 2;
    sum += d;
  }
  return sum % 10 === 0;
}
